;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { EMPLOYEE_PAYROLL_TYPES } from './constants';

export interface AmountInputCurrencyOption {
  id: number;
  code: string;
  name: string;
  flag: string;
}

export interface MonthlyInputCurrencyLike {
  id: number;
  code: string;
  name: string;
  flag_code: string;
}

export interface MonthlyInputPayrollCurrencySource {
  amount_currency_code?: string | null;
  expense_currency_code?: string | null;
  organisation_currency_code?: string | null;
  country_currency_code?: string | null;
  currency_id?: number | null;
}

function normalizeCurrencyCode(code: string | null | undefined): string {
  if (code == null || typeof code !== 'string') return '';
  const trimmed = code.trim();
  return trimmed.length > 0 ? trimmed.toUpperCase() : '';
}

export function mapCurrencyToAmountInputOption(
  currency: MonthlyInputCurrencyLike
): AmountInputCurrencyOption {
  return {
    code: currency.code,
    flag: `https://i.nativeteams.com/assets/app/flags_code/${currency.flag_code?.toUpperCase()}.svg`,
    id: currency.id,
    name: currency.name,
  };
}

/** All supported currencies for bonus/expense amount inputs, with optional preferred code first. */
export function buildMonthlyInputSelectableCurrencies(
  currencies: MonthlyInputCurrencyLike[],
  preferredCode?: string | null
): AmountInputCurrencyOption[] {
  const mapped = currencies.map(mapCurrencyToAmountInputOption);
  const preferred = normalizeCurrencyCode(preferredCode);
  if (!preferred) return mapped;

  const preferredIndex = mapped.findIndex(
    (currency) => normalizeCurrencyCode(currency.code) === preferred
  );
  if (preferredIndex <= 0) return mapped;

  const preferredCurrency = mapped[preferredIndex];
  if (!preferredCurrency) return mapped;

  return [preferredCurrency, ...mapped.filter((_, index) => index !== preferredIndex)];
}

interface AmountInputCurrencyRef {
  id?: number;
  code: string;
}

/** Resolves AmountInput's stored value (code, id, or "id-code-name") to a currency code. */
export function resolveAmountInputCurrencyCode(
  selectedValue: string | undefined,
  currencies?: AmountInputCurrencyRef[]
): string {
  const trimmed = selectedValue?.trim() ?? '';
  if (!trimmed) return '';

  if (currencies?.length) {
    const [identifier] = trimmed.split('-');
    const matched = currencies.find((currency) => {
      const code = currency.code.trim();
      if (!code) return false;
      if (currency.id != null && identifier === String(currency.id)) return true;

      const normalizedCode = code.toUpperCase();
      return (
        normalizedCode === identifier?.toUpperCase() || normalizedCode === trimmed.toUpperCase()
      );
    });
    if (matched?.code) return matched.code.trim();
  }

  const parts = trimmed.split('-');
  const codeFromComposite = parts[1];
  if (parts.length >= 2 && codeFromComposite && /^[A-Za-z]{3}$/.test(codeFromComposite)) {
    return codeFromComposite.toUpperCase();
  }

  const codeFromSimple = parts[0];
  if (codeFromSimple && /^[A-Za-z]{3}$/.test(codeFromSimple)) {
    return codeFromSimple.toUpperCase();
  }

  return trimmed;
}

export function resolveCurrencyIdByCode(
  currencyCode: string | undefined,
  currencies: AmountInputCurrencyRef[]
): number | undefined {
  const code = normalizeCurrencyCode(resolveAmountInputCurrencyCode(currencyCode, currencies));
  if (!code) return undefined;

  return currencies.find((currency) => normalizeCurrencyCode(currency.code) === code)?.id;
}

export function isMonthlyInputSelectableAmountPayrollType(
  payrollType: string | undefined
): boolean {
  return (
    payrollType === EMPLOYEE_PAYROLL_TYPES.BONUS || payrollType === EMPLOYEE_PAYROLL_TYPES.EXPENSE
  );
}

export function resolveMonthlyInputPayrollAmountCurrencyCode(
  payrollType: string | undefined,
  payroll: MonthlyInputPayrollCurrencySource | undefined,
  options?: {
    expenseCurrencyFallback?: string | null;
    countryCurrencyFallback?: string | null;
    currencies?: AmountInputCurrencyRef[];
  }
): string {
  const fromCurrencyId =
    payroll?.currency_id != null && options?.currencies?.length
      ? (options.currencies.find((currency) => currency.id === payroll.currency_id)?.code ?? '')
      : '';

  const amountCode = normalizeCurrencyCode(payroll?.amount_currency_code);
  const expenseCode = normalizeCurrencyCode(
    payroll?.expense_currency_code ?? options?.expenseCurrencyFallback
  );
  const organisationCode = normalizeCurrencyCode(payroll?.organisation_currency_code);
  const countryCode = normalizeCurrencyCode(
    payroll?.country_currency_code ?? options?.countryCurrencyFallback
  );
  const resolvedFromId = normalizeCurrencyCode(fromCurrencyId);

  if (payrollType === EMPLOYEE_PAYROLL_TYPES.BONUS) {
    return amountCode || resolvedFromId || expenseCode || organisationCode || countryCode;
  }

  if (payrollType === EMPLOYEE_PAYROLL_TYPES.EXPENSE) {
    return amountCode || resolvedFromId || expenseCode || countryCode;
  }

  return countryCode;
}

export function applyMonthlyInputAmountCurrencyId<T extends { payroll_type: string }>(
  payload: T,
  amountCurrency: string | undefined,
  currencies: AmountInputCurrencyRef[]
): T & { currency_id?: number } {
  if (!isMonthlyInputSelectableAmountPayrollType(payload.payroll_type)) {
    return payload;
  }

  const currency_id = resolveCurrencyIdByCode(amountCurrency, currencies);
  if (currency_id == null) return payload;

  return { ...payload, currency_id };
}
